/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1764115200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Nov 26 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mailchimp",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Data center",
          "depth": 1,
          "id": "data-center"
        }, {
          "value": "API key",
          "depth": 1,
          "id": "api-key"
        }, {
          "value": "How Hightouch connects",
          "depth": 0,
          "id": "how-hightouch-connects"
        }, {
          "value": "Syncing Mailchimp contacts",
          "depth": 0,
          "id": "syncing-mailchimp-contacts"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Delete mode",
          "depth": 1,
          "id": "delete-mode"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      code: "code",
      img: "img",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Object"
              })
            }), _jsx(_components.td, {
              children: "Sync members to audiences in Mailchimp."
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://mailchimp.com/developer/marketing/api/list-members/add-or-update-list-member/",
                children: "Members Docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Tag"
              })
            }), _jsx(_components.td, {
              children: "Create and apply a new tag or apply an existing tag to contacts within your audience."
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://mailchimp.com/developer/marketing/api/list-segments/batch-add-or-remove-members/",
                children: "Static Segments (Tags) Docs"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "To setup the Mailchimp destination, you will need your data center and an active API Key."
      }), "\n", _jsxs(_components.p, {
        children: ["To find the data center and API key, navigate to the ", _jsx(_components.a, {
          href: "https://admin.mailchimp.com",
          children: "Mailchimp Dashboard"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "data-center",
        children: "Data center"
      }), "\n", _jsxs(_components.p, {
        children: ["To find the data center, look at the prefix for your admin dashboard URL. In this example, the URL is ", _jsx(_components.code, {
          children: "us1.admin.mailchimp.com"
        }), ". Your prefix would be ", _jsx(_components.code, {
          children: "us1"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mailchimp-dashboard.png",
          alt: "In this example us1 is the data center"
        })
      }), "\n", _jsx(_components.h3, {
        id: "api-key",
        children: "API key"
      }), "\n", _jsxs(_components.p, {
        children: ["To find your API key, navigate to the ", _jsx(_components.a, {
          href: "https://admin.mailchimp.com/account/api/",
          children: "API Keys section"
        }), " of your Mailchimp account."]
      }), "\n", _jsx(_components.p, {
        children: "Go down to the section Your API Keys, and press the Create A Key button if none exist. Otherwise, you'll see your API key in the table."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mailchimp-apikey.png",
          alt: ""
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Mailchimp has API Keys and Mobile SDK API Keys. The Mobile SDK API Key will\nnot work with this integration. You will need to create a regular API Key."
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-hightouch-connects",
        children: "How Hightouch connects"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch connects to Mailchimp through the ", _jsx(_components.a, {
          href: "https://mailchimp.com/developer/marketing/api/",
          children: "Mailchimp Marketing API"
        }), ", specifically the ", _jsx(_components.a, {
          href: "https://mailchimp.com/developer/marketing/api/lists/",
          children: "Lists API"
        }), " to read, update, and manage audience data."]
      }), "\n", _jsx(_components.p, {
        children: "This integration handles:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Retrieving your available audiences for setup and testing"
        }), "\n", _jsx(_components.li, {
          children: "Syncing audience members and custom merge fields"
        }), "\n", _jsx(_components.li, {
          children: "Managing segment memberships"
        }), "\n", _jsx(_components.li, {
          children: "Archiving or permanently deleting members when records leave your query"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-mailchimp-contacts",
        children: "Syncing Mailchimp contacts"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Mailchimp resources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Audience members"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch requires an existing audience in Mailchimp to sync audience members."
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "This integration supports Upsert mode."
      }), "\n", _jsx(_components.p, {
        children: "In the Upsert mode, new users will be inserted into Mailchimp and all attributes will be kept up-to-date within Mailchimp."
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to your Mailchimp workspace by your Mailchimp Audience Member Email in the Upsert mode."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mailchimp-record-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Mailchimp's default and custom merge fields. Hightouch automatically detects existing merge fields from your Mailchimp audience."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-mailchimp-field-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "You can add custom merge fields in your Mailchimp dashboard from the Settings page dropdown and selecting Audience fields and *|MERGE|* tags"
      }), "\n", _jsx(_components.p, {
        children: "Once on the Audience fields page you can drop to the bottom of the page and hit the Add A Field button to create a custom merge field."
      }), "\n", _jsx(_components.h3, {
        id: "delete-mode",
        children: "Delete mode"
      }), "\n", _jsx(_components.p, {
        children: "You can choose what Hightouch's behavior is when records leave the query result set. The default is doing nothing, but you can also set Hightouch to archive or permanently delete the Mailchimp audience member on record exit."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ARCHIVE"
          }), " : Unsubscribe a user from your Mailchimp audience, but do not delete the record. Archived members can be re-added at a later date."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "DELETE"
          }), " : ", _jsx(_components.strong, {
            children: "Permanently"
          }), " remove an audience member and delete their information. This action can't be undone, and the user ", _jsx(_components.strong, {
            children: "can't"
          }), " be re-added to the list at a later date."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
